import { setViewModel } from './termsSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { NavigateParams } from '../../components/BtNavigate';
import { LoadingParams } from '../../components/BtLoading';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';

export function none(logoUrl: string): TermsScreen.None {
  return {
    type: 'none',
    texts: {
      body: '',
      metaTitle: '',
      title: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl,
      },
    },
  };
}

export namespace TermsScreen {
  interface Texts {
    body: string;
    metaTitle: string;
    title: string;
  }

  export interface None {
    type: 'none';
    texts: Texts;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    loading: LoadingParams;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    navigation: NavigateParams;
    body: string;
    metaTitle: string;
    title: string;
  }

  export type ViewModel = None | Loading | Ready;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    return { body: '', metaTitle: '', title: '' };
  }

  function loading(viewModel: ViewModel, logoUrl: string): Loading {
    const t = i18n.t;

    return {
      type: 'loading',
      texts: {
        body: t('termsScreen.terms'),
        metaTitle: t('termsScreen.metaTitle'),
        title: t('termsScreen.title'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
    };
  }

  function ready(viewModel: ViewModel, logoUrl: string): Ready {
    const texts = getTexts(viewModel);

    return {
      type: 'ready',
      texts: texts,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      body: texts.body,
      title: texts.title,
      metaTitle: texts.metaTitle,
    };
  }

  export function start(
    viewModel: ViewModel,
    logoUrl: string,
  ): (dispatch: Dispatch<AnyAction>) => void {
    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel, logoUrl)));
    };
  }

  export async function load(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(ready(viewModel, logoUrl)));
    };
  }

  export async function close(
    viewModel: ViewModel,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none('')));
    };
  }
}
